import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBulkDropdownData } from "../../api/keyvalue";
import { AlertCircle, Shield, Search } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import SearchDropdown from "../../components/input/SearchDropdown";
import { countryStartsWith } from "../../api/countryApi";
import { stateByCountryAndStartsWith } from "../../api/stateApi";
import {
  ActiveCellStyle,
  StatusCellStyle,
  AsyncOtherRefDropdownValueRenderer,
} from "../../components/renderer/StyleRenderer";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import PoliceDivisionAddComponent from "../../components/policeDivision/PoliceDivisionAddComponent";
import {
  addPoliceDivisionData,
  searchPoliceDivision,
  updatePoliceDivisionData,
  getPoliceDivisionDataById,
} from "../../api/policeDivisionApi";
import { errorActions } from "../../store/error-slice";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";

const FORM = FORMS.POLICE_DIVISION.POL.form;

const PoliceDivision = () => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.POLICE_DIVISION,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [isCountrySet, setIsCountrySet] = useState(false);
  const [isStateSet, setIsStateSet] = useState(false);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const dispatch = useDispatch();
  const errorState = useSelector((state) => state.error.policeDivisionErrors);
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [editPoliceDivision, setEditPoliceDivision] = useState({});
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdowns
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusOpt, setStatusOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);

  const policeDivisionRef = useRef();
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const policeStationRef = useRef(null);

  const user = useSelector((state) => state.user);
  const [initialActive, setInitialActive] = useState(1);

  useEffect(() => {
    if (editMode && editPoliceDivision) {
      setInitialActive(editPoliceDivision.rpoActive);
    }
  }, [editMode, editPoliceDivision]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { active, status } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
      ]);
      setActiveOptions(active);
      setActiveOpt(active);
      setStatusOptions(status);
      setStatusOpt(status);
      setIsLoading(false);
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditPoliceDivision(null);
    setDrawerTab(1);
    dispatch(errorActions.clearPoliceDivisionError());
    policeDivisionRef.current?.resetForm();
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    dispatch(errorActions.clearPoliceDivisionError());
    policeDivisionRef.current?.resetForm();
  };

  const reset = () => {
    dispatch(errorActions.clearPoliceDivisionError());
    policeDivisionRef.current?.resetForm();
    setEditMode(false);
    setEditPoliceDivision(null);
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();

    const data = policeDivisionRef.current.getData();
    const isDeletedActive =
      editMode && Number(data.rpoActive) === 2 && Number(initialActive) !== 2;

    if (!bypassDeleteCheck && isDeletedActive) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }

    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearPoliceDivisionError());

    const validationErrors = policeDivisionRef.current.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      dispatch(
        errorActions.validatePoliceDivision({
          policeDivisionRequest: data,
        }),
      );
      setIsSubmitLoading(false);
      toast.error("Please fill all required fields correctly.", {
        autoClose: 5000,
        containerId: "Main",
      });
    } else {
      try {
        if (editMode) {
          await updatePoliceDivisionData({
            ...data,
            lastModifiedBy: user?.username || "Admin",
            author: user?.username || "Admin",
          });
          toast.success("Police Division updated successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          await addPoliceDivisionData({
            ...data,
            createdBy: user?.username || "Admin",
            author: user?.username || "Admin",
          });
          toast.success("Police Division added successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        handleDrawerClose();
        reset();
        if (searchClicked) {
          handleSearchSubmit();
        }
      } catch (err) {
        console.error("Error saving police division:", err);
        if (err.response && err.response.status === 409) {
          const msg =
            (typeof err.response.data === "string" && err.response.data) ||
            err.response.data?.message ||
            "Police Division already exists!";
          const isCodeError =
            typeof msg === "string" && msg.toLowerCase().includes("code");
          dispatch(
            errorActions.addPoliceDivisionErrorObject({
              form: "policeDivisionErrors",
              errors: isCodeError ? { rpoCode: msg } : { rpoName: msg },
            }),
          );
          toast.error(msg, {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          const msg =
            (typeof err.response?.data === "string" && err.response.data) ||
            err.response?.data?.message ||
            err.message ||
            "An error occurred while saving the Police Division details.";
          console.error("Police Division save error details:", {
            status: err.response?.status,
            data: err.response?.data,
            message: err.message,
          });
          toast.error(msg, {
            autoClose: 5000,
            containerId: "Main",
          });
        }
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };

  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();

    if (tab === 1) {
      setIsLoading(false);
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const response = await searchPoliceDivision({
      rpoCode: codeRef.current ? codeRef.current.value.trim() : "",
      rpoName: nameRef.current ? nameRef.current.value.trim() : "",
      rpoPoliceStation: policeStationRef.current
        ? policeStationRef.current.value.trim()
        : "",
      rpoStatus: status,
      rpoActive: active,
      rpoCountry: country || null,
      rpoState: state || null,
    });
    const mappedResults = (response || []).map((item) => {
      const statusOption = statusOptions.find(
        (o) =>
          Number(o.value) === Number(item.rpoStatus) ||
          o.key === String(item.rpoStatus),
      );
      const activeOption = activeOptions.find(
        (o) =>
          Number(o.value) === Number(item.rpoActive) ||
          o.key === String(item.rpoActive),
      );
      return {
        ...item,
        rpoCode: item.rpoCode || "",
        statusText: statusOption ? statusOption.text : "Pending",
        activeText: activeOption ? activeOption.text : "Active",
      };
    });
    setSearchRes(mappedResults);
    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    codeRef.current.value = "";
    nameRef.current.value = "";
    policeStationRef.current.value = "";
    setStatus(null);
    setActive(null);
    setCountry("");
    setState("");
    setIsCountrySet(false);
    setIsStateSet(false);
    setSearchFieldReset(true);
    setSearchRes([]);
    setSearchClicked(false);
    setResetVisible(false);
    setActiveAccordion1(true);
  };

  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Shield size={15} style={{ marginRight: "5px" }} />
            </span>
            Police Division Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            onClick={handleDrawerOpen}
            authenticated={false}
            editMode={false}
          />
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  handleSearchReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui dividing header">
                  Police Division Information
                </h4>
                <div className="four fields">
                  <div className="field">
                    <label>Police Division Code</label>
                    <input
                      type="text"
                      placeholder="Division Code"
                      name="rpoCode"
                      ref={codeRef}
                    />
                  </div>

                  <div className="field">
                    <label>Police Division Name</label>
                    <input
                      type="text"
                      placeholder="Division Name"
                      name="rpoName"
                      ref={nameRef}
                    />
                  </div>

                  <div className="field">
                    <label>Police Station</label>
                    <input
                      type="text"
                      placeholder="Police Station"
                      name="rpoPoliceStation"
                      ref={policeStationRef}
                    />
                  </div>

                  <SearchDropdown
                    key={1}
                    editMode={false}
                    threshold={1}
                    reset={searchFieldReset}
                    setReset={setSearchFieldReset}
                    onResultSelect={(data) => {
                      setIsCountrySet(true);
                      setCountry(data);
                    }}
                    onClear={() => {
                      setCountry("");
                      setIsCountrySet(false);
                      setState("");
                      setIsStateSet(false);
                    }}
                    api={countryStartsWith}
                    fieldInfo={{
                      tableName: "masterdata.tms_m_reg_country",
                      primaryKey: "cou_id",
                      fieldName: "cou_country",
                      id: country,
                    }}
                    label="Country"
                  />
                </div>

                <div className="four fields">
                  <SearchDropdown
                    key={2}
                    editMode={false}
                    reset={searchFieldReset}
                    setReset={setSearchFieldReset}
                    onResultSelect={(data) => {
                      setIsStateSet(true);
                      setState(data);
                    }}
                    onClear={() => {
                      setState("");
                      setIsStateSet(false);
                    }}
                    api={stateByCountryAndStartsWith}
                    apiDependency={{ id: country }}
                    fieldInfo={{
                      tableName: "masterdata.tms_m_reg_state",
                      primaryKey: "stt_id",
                      fieldName: "stt_state_name",
                      id: state,
                    }}
                    label="State"
                    disabled={!isCountrySet}
                  />

                  <div className="field">
                    <label>Status</label>
                    <Select
                      clearable
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>Active</label>
                    <Select
                      clearable
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Police Division Code",
                    field: "rpoCode",
                    sort: "desc",
                    width: 120,
                  },
                  {
                    headerName: "Name",
                    field: "rpoName",
                  },
                  {
                    headerName: "Country",
                    field: "rpoCountry",
                    cellRenderer: AsyncOtherRefDropdownValueRenderer,
                    cellRendererParams: {
                      tableName: "masterdata.tms_m_reg_country",
                      primaryKey: "cou_id",
                      fieldName: "cou_country",
                    },
                  },
                  {
                    headerName: "State",
                    field: "rpoState",
                    cellRenderer: AsyncOtherRefDropdownValueRenderer,
                    cellRendererParams: {
                      tableName: "masterdata.tms_m_reg_state",
                      primaryKey: "stt_id",
                      fieldName: "stt_state_name",
                    },
                  },
                  {
                    headerName: "Police Station",
                    field: "rpoPoliceStation",
                  },
                  {
                    headerName: "Status",
                    field: "statusText",
                    cellRenderer: StatusCellStyle,
                    width: 100,
                  },
                  {
                    headerName: "Active",
                    field: "activeText",
                    cellRenderer: ActiveCellStyle,
                    width: 100,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  try {
                    const response = await getPoliceDivisionDataById(
                      props.data.rpoId,
                    );
                    const resData = response || props.data;
                    dispatch(errorActions.clearPoliceDivisionError());
                    policeDivisionRef.current?.resetForm();
                    setEditPoliceDivision({
                      ...resData,
                      rpoCode: resData?.rpoCode || props.data?.rpoCode,
                    });
                    setEditMode(true);
                    setIsDrawerOpen(true);
                  } catch (err) {
                    console.error("Error loading police division by ID", err);
                  } finally {
                    setSearchFetchLoading(false);
                  }
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="police-division-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>Police Division Code: {editPoliceDivision?.rpoCode}</h3>
            </div>
          ) : null
        }
        body={
          <>
            <form className="drawer-content" onSubmit={handleSubmit}>
              <div style={{ width: "60vw", boxShadow: "none" }}>
                <div className="ui top attached tabular menu">
                  <Tab
                    Icon={Shield}
                    onClick={() => {
                      setDrawerTab(1);
                    }}
                    active={drawerTab === 1}
                    errors={
                      Object.keys(errorState.policeDivisionErrors || {})
                        .length !== 0
                    }
                    label="Police Division"
                    authenticated={false}
                    showMandatory={true}
                    isMandatory={true}
                  />
                </div>
                <div
                  className="ui bottom attached active tab segment"
                  style={{ display: drawerTab === 1 ? "block" : "none" }}
                >
                  <PoliceDivisionAddComponent
                    ref={policeDivisionRef}
                    isLoading={isLoading}
                    errors={errorState.policeDivisionErrors}
                    dropdown={{
                      statusOpt,
                      activeOpt,
                    }}
                    editMode={editMode}
                    currentValues={editMode ? editPoliceDivision : null}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      authenticated={false}
                      editMode={editMode}
                    />
                  </div>
                </div>
              </div>
            </form>
            <Modal
              ref={deleteWarningRef}
              closeButton={false}
              minWidth={"300px"}
            >
              {showDeleteWarning && deleteWarningModal}
            </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default PoliceDivision;
